import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useGaContext } from "@/hooks/useGaContext";
import {
  Building2, Plus, Pencil, Trash2, CheckCircle2, Circle, Ban,
  ChevronRight, GripVertical, Save, X, Lock, ShieldCheck,
  FileText, AlertTriangle,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────
type ChecklistTemplateItem = {
  id: string;
  label: string;
  category: string;
  required: boolean;
  notes: string;
};

type CarrierConfig = {
  id: string;
  gaId: string;
  carrierName: string;
  displayName: string | null;
  minParticipationPct: number;
  minLives: number;
  uwGuidelines: string | null;
  checklistItems: ChecklistTemplateItem[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

// ─── Default carriers to seed ─────────────────────────────────────────────────
const COMMON_CARRIERS = [
  "Anthem Blue Cross", "Blue Shield of California", "Kaiser Permanente",
  "UnitedHealthcare", "Aetna", "Cigna", "Health Net",
  "Sutter Health Plus", "Sharp Health Plan",
];

// ─── Default doc categories ───────────────────────────────────────────────────
const DOC_CATEGORIES = [
  "Application", "Census", "Carrier Forms", "Invoice",
  "Tax/Payroll", "Document", "COBRA", "SOH / APS", "Other",
];

// ─── Checklist item status icon ───────────────────────────────────────────────
function RequiredBadge({ required }: { required: boolean }) {
  return required
    ? <span className="text-[10px] bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 border border-red-100 dark:border-red-800 rounded px-1.5 py-0.5 font-medium">Required</span>
    : <span className="text-[10px] bg-muted text-muted-foreground border border-border rounded px-1.5 py-0.5">Optional</span>;
}

// ─── Carrier Editor Panel ─────────────────────────────────────────────────────
function CarrierEditor({
  carrier, gaId, onSaved, onDelete,
}: {
  carrier: CarrierConfig; gaId: string;
  onSaved: () => void; onDelete: () => void;
}) {
  const { toast } = useToast();

  // Form state (mirrors the carrier, local until saved)
  const [carrierName, setCarrierName]       = useState(carrier.carrierName);
  const [displayName, setDisplayName]       = useState(carrier.displayName || "");
  const [minPct, setMinPct]                 = useState(carrier.minParticipationPct);
  const [minLives, setMinLives]             = useState(carrier.minLives);
  const [guidelines, setGuidelines]         = useState(carrier.uwGuidelines || "");
  const [items, setItems]                   = useState<ChecklistTemplateItem[]>(carrier.checklistItems || []);
  const [dirty, setDirty]                   = useState(false);

  // New item form
  const [newLabel, setNewLabel]             = useState("");
  const [newCat, setNewCat]                 = useState("Document");
  const [newRequired, setNewRequired]       = useState(true);
  const [newNotes, setNewNotes]             = useState("");
  const [addingItem, setAddingItem]         = useState(false);

  const [confirmDelete, setConfirmDelete]   = useState(false);
  const [editNoteId, setEditNoteId]         = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: () => apiRequest("PATCH", `/api/ga/${gaId}/carrier-configs/${carrier.id}`, {
      carrierName, displayName: displayName || null,
      minParticipationPct: minPct, minLives,
      uwGuidelines: guidelines || null, checklistItems: items,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "carrier-configs"] });
      setDirty(false);
      toast({ title: "Carrier settings saved" });
      onSaved();
    },
    onError: () => toast({ title: "Error saving", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiRequest("DELETE", `/api/ga/${gaId}/carrier-configs/${carrier.id}`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "carrier-configs"] });
      toast({ title: "Carrier removed" });
      onDelete();
    },
    onError: () => toast({ title: "Error removing", variant: "destructive" }),
  });

  const toggleActive = useMutation({
    mutationFn: () => apiRequest("PATCH", `/api/ga/${gaId}/carrier-configs/${carrier.id}`, { isActive: !carrier.isActive }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "carrier-configs"] }),
  });

  const mark = () => setDirty(true);

  const addItem = () => {
    if (!newLabel.trim()) return;
    setItems((prev) => [...prev, {
      id: `item-${Date.now()}`, label: newLabel.trim(),
      category: newCat, required: newRequired, notes: newNotes.trim(),
    }]);
    setNewLabel(""); setNewNotes(""); setNewRequired(true); setAddingItem(false);
    mark();
  };

  const removeItem = (id: string) => { setItems((prev) => prev.filter((i) => i.id !== id)); mark(); };
  const toggleRequired = (id: string) => {
    setItems((prev) => prev.map((i) => i.id === id ? { ...i, required: !i.required } : i));
    mark();
  };
  const updateItemNote = (id: string, notes: string) => {
    setItems((prev) => prev.map((i) => i.id === id ? { ...i, notes } : i));
    mark();
  };

  const required = items.filter((i) => i.required).length;
  const optional = items.filter((i) => !i.required).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <h2 className="text-lg font-semibold text-foreground">{carrier.displayName || carrier.carrierName}</h2>
          {!carrier.isActive && (
            <span className="text-xs bg-muted text-muted-foreground rounded px-2 py-0.5">Inactive</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm" variant="ghost"
            className="h-7 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => toggleActive.mutate()}
            disabled={toggleActive.isPending}
          >
            {carrier.isActive ? "Deactivate" : "Activate"}
          </Button>
          <Button
            size="sm" variant="ghost"
            className="h-7 text-xs text-red-400 dark:text-red-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950"
            onClick={() => setConfirmDelete(true)}
          >
            <Trash2 className="w-3 h-3 mr-1" /> Remove
          </Button>
        </div>
      </div>

      {/* ── Basic Settings ── */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5 col-span-2 md:col-span-1">
          <Label className="text-xs">Carrier Name <span className="text-red-500 dark:text-red-400">*</span></Label>
          <Input value={carrierName} onChange={(e) => { setCarrierName(e.target.value); mark(); }}
            placeholder="e.g. Anthem Blue Cross" className="h-8 text-sm" />
        </div>
        <div className="space-y-1.5 col-span-2 md:col-span-1">
          <Label className="text-xs">Display Name <span className="text-muted-foreground font-normal">(optional short name)</span></Label>
          <Input value={displayName} onChange={(e) => { setDisplayName(e.target.value); mark(); }}
            placeholder="e.g. Anthem" className="h-8 text-sm" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Min Participation %</Label>
          <Input type="number" min={0} max={100} value={minPct}
            onChange={(e) => { setMinPct(parseInt(e.target.value) || 0); mark(); }}
            className="h-8 text-sm" />
          <p className="text-[10px] text-muted-foreground">This pre-fills the participation calculator when this carrier is selected on a submission.</p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Min Enrolled Lives</Label>
          <Input type="number" min={1} value={minLives}
            onChange={(e) => { setMinLives(parseInt(e.target.value) || 1); mark(); }}
            className="h-8 text-sm" />
        </div>
      </div>

      {/* ── UW Guidelines ── */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-1.5">
          <Lock className="w-3.5 h-3.5 text-muted-foreground" />
          <Label className="text-xs">Underwriting Guidelines</Label>
          <span className="text-[10px] text-muted-foreground">(internal — visible only to UW team)</span>
        </div>
        <Textarea
          value={guidelines}
          onChange={(e) => { setGuidelines(e.target.value); mark(); }}
          rows={5}
          className="text-sm resize-none"
          placeholder={`Rate-up rules, special risk classes, late entrant requirements, tobacco surcharges, waiting periods, state-specific rules…\n\nExample:\n• 70% participation required (spouses / domestic partners on spouse plan excluded)\n• SOH required for late entrants and for any group with 5+ tobacco users\n• No rate-ups in CA for small groups`}
        />
      </div>

      {/* ── Document Checklist Template ── */}
      <div className="border-t pt-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-semibold text-foreground">Document Checklist Template</span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              These items will populate the checklist when this carrier is loaded on a submission.
              {items.length > 0 && (
                <span className="ml-1">
                  <span className="text-red-500 dark:text-red-400">{required} required</span>
                  {optional > 0 && <span className="text-muted-foreground"> · {optional} optional</span>}
                </span>
              )}
            </p>
          </div>
          <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-primary"
            onClick={() => setAddingItem(true)}>
            <Plus className="w-3 h-3" /> Add Item
          </Button>
        </div>

        {items.length === 0 && !addingItem ? (
          <div className="text-center py-8 border border-dashed border-border rounded-lg text-muted-foreground">
            <FileText className="w-6 h-6 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No template items yet</p>
            <p className="text-xs mt-1">Add documents the UW team requires from the broker for this carrier</p>
            <Button size="sm" variant="outline" className="mt-3" onClick={() => setAddingItem(true)}>
              <Plus className="w-3 h-3 mr-1" /> Add First Item
            </Button>
          </div>
        ) : (
          <div className="rounded-lg border border-border overflow-hidden divide-y divide-border">
            {items.map((item) => (
              <div key={item.id} className="flex items-center gap-2 px-3 py-2 group hover:bg-muted/50">
                {/* Required toggle */}
                <button
                  onClick={() => toggleRequired(item.id)}
                  title={item.required ? "Mark as optional" : "Mark as required"}
                  className={`w-5 h-5 flex items-center justify-center rounded border shrink-0 transition-colors ${
                    item.required
                      ? "bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800 text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900"
                      : "bg-muted border-border text-muted-foreground hover:bg-muted/70"
                  }`}
                >
                  {item.required ? <CheckCircle2 className="w-3 h-3" /> : <Circle className="w-3 h-3" />}
                </button>

                {/* Label + note */}
                <div className="flex-1 min-w-0">
                  <span className="text-xs text-foreground">{item.label}</span>
                  {(editNoteId === item.id || item.notes) && (
                    <input
                      autoFocus={editNoteId === item.id && !item.notes}
                      type="text"
                      value={item.notes}
                      onChange={(e) => updateItemNote(item.id, e.target.value)}
                      onBlur={() => setEditNoteId(null)}
                      placeholder="Note…"
                      className="mt-0.5 w-full text-[10px] text-muted-foreground bg-transparent border-0 border-b border-border focus:border-blue-300 dark:focus:border-blue-700 focus:outline-none py-0 placeholder:text-muted-foreground"
                    />
                  )}
                </div>

                {/* Category + required badge */}
                <div className="flex items-center gap-1.5 shrink-0">
                  <RequiredBadge required={item.required} />
                  <span className="text-[10px] text-muted-foreground bg-muted rounded px-1.5 py-0.5 hidden sm:block">
                    {item.category}
                  </span>
                </div>

                {/* Actions on hover */}
                <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  {editNoteId !== item.id && !item.notes && (
                    <button onClick={() => setEditNoteId(item.id)}
                      className="text-[10px] text-muted-foreground hover:text-foreground px-1">
                      Note
                    </button>
                  )}
                  <button onClick={() => removeItem(item.id)} className="text-muted-foreground hover:text-red-400 dark:hover:text-red-500">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add item inline form */}
        {addingItem && (
          <div className="mt-2 p-3 rounded-lg border border-dashed border-blue-200 dark:border-blue-800 bg-blue-50/30 dark:bg-blue-950/30 space-y-2">
            <div className="flex items-center gap-2">
              <input
                autoFocus
                type="text"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addItem(); if (e.key === "Escape") setAddingItem(false); }}
                placeholder="Document name…"
                className="flex-1 h-7 text-xs border border-border rounded px-2 bg-card focus:outline-none focus:border-blue-300 dark:focus:border-blue-700"
              />
              <Select value={newCat} onValueChange={setNewCat}>
                <SelectTrigger className="h-7 text-xs w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOC_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={newNotes}
                onChange={(e) => setNewNotes(e.target.value)}
                placeholder="Notes for UW team (optional)…"
                className="flex-1 h-7 text-xs border border-border rounded px-2 bg-card focus:outline-none focus:border-blue-300 dark:focus:border-blue-700"
              />
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer shrink-0">
                <input type="checkbox" checked={newRequired} onChange={(e) => setNewRequired(e.target.checked)}
                  className="w-3.5 h-3.5" />
                Required
              </label>
              <Button size="sm" className="h-7 text-xs shrink-0" onClick={addItem} disabled={!newLabel.trim()}>Add</Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs shrink-0" onClick={() => setAddingItem(false)}>Cancel</Button>
            </div>
          </div>
        )}
      </div>

      {/* ── Save footer ── */}
      <div className="flex items-center justify-between pt-2 border-t">
        <p className="text-xs text-muted-foreground">{dirty ? "You have unsaved changes" : "All changes saved"}</p>
        <Button
          className="h-8 text-sm gap-1.5"
          onClick={() => saveMutation.mutate()}
          disabled={!dirty || !carrierName.trim() || saveMutation.isPending}
        >
          <Save className="w-3.5 h-3.5" />
          {saveMutation.isPending ? "Saving…" : "Save Carrier Settings"}
        </Button>
      </div>

      {/* Confirm delete */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {carrier.carrierName}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete this carrier's UW guidelines and document checklist template. Submissions that have already loaded this carrier's checklist will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteMutation.mutate()}>
              Remove Carrier
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function GACarrierSettings() {
  const { gaId } = useGaContext();
  const { toast } = useToast();

  const [selectedId, setSelectedId]     = useState<string | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newCarrierName, setNewCarrierName] = useState("");
  const [newCarrierPreset, setNewCarrierPreset] = useState("");

  const { data: carriers = [], isLoading } = useQuery<CarrierConfig[]>({
    queryKey: ["/api/ga", gaId, "carrier-configs"],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/carrier-configs`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!gaId,
  });

  const addMutation = useMutation({
    mutationFn: (data: any) => apiRequest("POST", `/api/ga/${gaId}/carrier-configs`, data),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "carrier-configs"] });
      setShowAddDialog(false);
      setNewCarrierName("");
      setNewCarrierPreset("");
      setSelectedId(data.id);
      toast({ title: "Carrier added" });
    },
    onError: () => toast({ title: "Error adding carrier", variant: "destructive" }),
  });

  const selectedCarrier = carriers.find((c) => c.id === selectedId) ?? null;

  // Auto-select first carrier if none selected
  const effectiveSelected = selectedCarrier ?? (carriers.length > 0 ? carriers[0] : null);

  const handleAdd = () => {
    const name = newCarrierPreset || newCarrierName.trim();
    if (!name) return;
    addMutation.mutate({
      carrierName: name,
      minParticipationPct: 75,
      minLives: 2,
      checklistItems: [],
    });
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Carrier Settings</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Per-carrier UW guidelines and document checklist templates for the underwriting workbench
          </p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} className="gap-1.5">
          <Plus className="w-4 h-4" /> Add Carrier
        </Button>
      </div>

      {/* Info banner */}
      <div className="rounded-lg border border-blue-100 dark:border-blue-900 bg-blue-50/40 dark:bg-blue-950/40 px-4 py-3 flex items-start gap-3">
        <ShieldCheck className="w-4 h-4 text-blue-500 dark:text-blue-400 shrink-0 mt-0.5" />
        <p className="text-sm text-blue-700 dark:text-blue-400">
          <span className="font-medium">How this works:</span> When the UW team selects a carrier on a case submission,
          they can load that carrier's document checklist template and participation requirements directly into the case — no manual setup needed.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      ) : carriers.length === 0 ? (
        <Card>
          <CardContent className="text-center py-20 text-muted-foreground">
            <Building2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-base font-medium">No carriers configured yet</p>
            <p className="text-sm mt-1 mb-6">
              Add the carriers you work with to set up UW guidelines and document checklists.
            </p>
            <Button onClick={() => setShowAddDialog(true)} className="gap-1.5">
              <Plus className="w-4 h-4" /> Add Your First Carrier
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ── Left: Carrier list ── */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
              Configured Carriers ({carriers.length})
            </p>
            {carriers.map((carrier) => {
              const isActive = carrier.id === (effectiveSelected?.id);
              const itemCount = (carrier.checklistItems || []).length;
              return (
                <button
                  key={carrier.id}
                  onClick={() => setSelectedId(carrier.id)}
                  className={`w-full text-left rounded-lg border px-3 py-3 transition-all ${
                    isActive
                      ? "border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-950 shadow-sm"
                      : "border-border bg-card hover:border-muted-foreground/30 hover:bg-muted/50"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className={`text-sm font-medium truncate ${isActive ? "text-blue-800 dark:text-blue-300" : "text-foreground"}`}>
                        {carrier.displayName || carrier.carrierName}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {carrier.minParticipationPct}% participation · {carrier.minLives} min lives
                        {itemCount > 0 && (
                          <span className="ml-1 text-muted-foreground">· {itemCount} doc{itemCount !== 1 ? "s" : ""}</span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {!carrier.isActive && (
                        <span className="text-[10px] bg-muted text-muted-foreground rounded px-1.5">Off</span>
                      )}
                      <ChevronRight className={`w-4 h-4 ${isActive ? "text-blue-400 dark:text-blue-500" : "text-muted-foreground"}`} />
                    </div>
                  </div>
                </button>
              );
            })}
            <Button variant="outline" size="sm" className="w-full text-xs gap-1 mt-1"
              onClick={() => setShowAddDialog(true)}>
              <Plus className="w-3 h-3" /> Add Carrier
            </Button>
          </div>

          {/* ── Right: Editor ── */}
          <div className="lg:col-span-2">
            {effectiveSelected ? (
              <Card>
                <CardContent className="pt-5">
                  <CarrierEditor
                    key={effectiveSelected.id}
                    carrier={effectiveSelected}
                    gaId={gaId || ""}
                    onSaved={() => {}}
                    onDelete={() => setSelectedId(null)}
                  />
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="flex items-center justify-center py-20 text-muted-foreground">
                  <p className="text-sm">Select a carrier to edit its settings</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* ── Add Carrier Dialog ── */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-blue-600 dark:text-blue-400" /> Add Carrier
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="space-y-1.5">
              <Label className="text-xs">Choose from common carriers</Label>
              <Select value={newCarrierPreset} onValueChange={(v) => { setNewCarrierPreset(v); setNewCarrierName(""); }}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Select a carrier…" />
                </SelectTrigger>
                <SelectContent>
                  {COMMON_CARRIERS
                    .filter((c) => !carriers.find((cfg) => cfg.carrierName === c))
                    .map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  <SelectItem value="__other">Other / custom…</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(newCarrierPreset === "__other" || (!newCarrierPreset)) && (
              <div className="space-y-1.5">
                <Label className="text-xs">Or enter a carrier name</Label>
                <Input
                  autoFocus={!newCarrierPreset}
                  placeholder="e.g. Molina Healthcare"
                  className="h-9"
                  value={newCarrierName}
                  onChange={(e) => { setNewCarrierName(e.target.value); setNewCarrierPreset(""); }}
                  onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                />
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              You'll set up UW guidelines and the document checklist after adding.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddDialog(false); setNewCarrierName(""); setNewCarrierPreset(""); }}>
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={(!newCarrierPreset || newCarrierPreset === "__other") && !newCarrierName.trim() || addMutation.isPending}
            >
              {addMutation.isPending ? "Adding…" : "Add Carrier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
